import _ from 'lodash';
import { sqModalSearchStore, sqSearchStore, sqWorkbenchStore } from '@/core/core.stores';
import {
  API_TYPES,
  CREATED_BY_SEEQ_WORKBENCH,
  ITEM_TABLE_TYPE,
  SCALING_TABLE_TYPE,
  SEARCH_TYPES,
} from '@/main/app.constants';
import { cancelGroup } from '@/requests/pendingRequests.utilities';
import {
  ASSET_TREE_SEARCH_LIMIT,
  HOME_BREADCRUMB,
  HierarchyNodeType,
  MAX_RETURNED_RESULTS,
  SEARCH_BREADCRUMB,
  SEARCH_PER_PAGE,
  SEARCH_USAGES_BREADCRUMB,
} from '@/search/search.constants';
import { nodeAndPathToSelectedSearchItem, nodeToSelectedSearchItem, toAncestorItem } from '@/search/search.utilities';
import { addAssetsProperty } from '@/utilities/httpHelpers.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { isPresentationWorkbookMode } from '@/utilities/workbookMode.utilities';
import { fetchAssetGroups, fetchPinnedItems } from '@/workbook/workbook.actions';
import { sqDatasourcesApi } from '@seeqdev/api/endpoint/DatasourcesApi';
import { sqDisplayTemplatesApi } from '@seeqdev/api/endpoint/DisplayTemplatesApi';
import { sqDisplaysApi } from '@seeqdev/api/endpoint/DisplaysApi';
import { sqHierarchiesApi } from '@seeqdev/api/endpoint/HierarchiesApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqTablesApi } from '@seeqdev/api/endpoint/TablesApi';
import { sqTreesApi } from '@seeqdev/api/endpoint/TreesApi';
import { OrderByFieldsEnum } from '@seeqdev/api/model/ItemSearchInputV1';
import { SeeqNames } from '@seeqdev/constants';
import { validateGuid } from '@seeqdev/utils';
import type { AxiosResponse } from 'axios';
import type { TOptions } from 'i18next';
import type { SearchModes, SearchPanes } from '@/search/search.constants';
import type { SearchBreadcrumb, SelectedSearchItem } from '@/search/search.types';
import type { AssetTreeOutputV1 } from '@seeqdev/api/model/AssetTreeOutputV1';
import type { HierarchyNodeOutputV1 } from '@seeqdev/api/model/HierarchyNodeOutputV1';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';

export const cancellationGroup = 'dataSearch';

const toBreadcrumb = <T extends { id: string; name?: string; type?: string }>(item: T): T & SearchBreadcrumb => ({
  ...item,
  name: item.name!,
  type: item.type!,
});

/**
 * Initializes the specified search pane with assets or results and the breadcrumbs.
 */
export function initializeSearchActions(
  pane: SearchPanes,
  searchTypes: string[],
  restrictExploration: boolean,
  scopeIds: string[],
  excludeGloballyScoped = false,
  initialItemTable?: SelectedSearchItem,
  initialHierarchy?: SelectedSearchItem,
  initialScalingTable?: SelectedSearchItem,
): Promise<unknown> {
  if (isPresentationWorkbookMode() && pane === 'main') {
    return Promise.resolve();
  }
  const store = getStoreForPane(pane);
  const {
    mode,
    breadcrumbs,
    selectedItemTable,
    currentHierarchyPath,
    selectedHierarchy,
    currentAsset,
    typeFilter,
    selectedScalingTable,
    searchLocallyScoped,
  } = store.getState();
  const { setAsyncInitialized, setBreadcrumbs, exploreItemHierarchy, appendOrReplaceBreadcrumb, setMode } =
    store.actions;

  setAsyncInitialized(true);
  if (mode === 'usages' && !_.isEmpty(breadcrumbs)) {
    const breadcrumbIds = _.flatMap(breadcrumbs.slice(2), 'id');
    return Promise.all(_.map(breadcrumbIds, (id) => sqItemsApi.getItemAndAllProperties({ id })))
      .then((results) =>
        _.chain(results)
          .map('data')
          .map((item) => toBreadcrumb({ id: item.id, name: item.name, type: item.type }))
          .value(),
      )
      .then((breadcrumbs) => {
        setBreadcrumbs([HOME_BREADCRUMB, SEARCH_USAGES_BREADCRUMB].concat(breadcrumbs));
        const lastBreadcrumb = _.last(breadcrumbs);
        if (lastBreadcrumb) {
          return fetchItemUsages(lastBreadcrumb, pane, _.compact([sqWorkbenchStore.stateParams.workbookId]), false);
        }
      });
  }

  if (mode === 'scaling-tables-tree') {
    const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
    if (lastBreadcrumb) {
      void fetchScalingTableSearchItems(pane, lastBreadcrumb);
    }
  }

  if (mode === 'item-hierarchies') {
    const itemTable = selectedItemTable ?? initialItemTable;
    exploreItemHierarchy();

    if (itemTable) {
      fetchHierarchiesForSearchResults(pane, itemTable);
    }
  }

  if (initialItemTable) {
    appendOrReplaceBreadcrumb(HOME_BREADCRUMB);
    setUpHierarchiesSearchForItemTable(pane, initialItemTable);
  }

  if (initialHierarchy) {
    appendOrReplaceBreadcrumb(HOME_BREADCRUMB);
    setMode('hierarchy-nodes');
    exploreHierarchyNode(pane, initialHierarchy.id);
  }

  if (initialScalingTable) {
    appendOrReplaceBreadcrumb(HOME_BREADCRUMB);
    setMode('scaling-tables-tree');
    fetchScalingTableSearchItems(pane, initialScalingTable);
  }

  if (mode === 'hierarchy-nodes') {
    exploreItemHierarchy();
    if (selectedHierarchy && currentHierarchyPath.length === 0) {
      loadHierarchyItems(selectedHierarchy.id, selectedHierarchy, currentHierarchyPath, pane);
    }
    if (selectedHierarchy && currentHierarchyPath.length > 0) {
      const leafNode = currentHierarchyPath.at(-1);
      if (leafNode) {
        const leafItem = nodeAndPathToSelectedSearchItem(leafNode, currentHierarchyPath, selectedHierarchy);
        loadHierarchyItems(selectedHierarchy.id, leafItem, currentHierarchyPath, pane);
      }
    }
  }

  if (mode === 'search' && !_.isEmpty(currentAsset)) {
    let breadcrumbs: SearchBreadcrumb[] = [HOME_BREADCRUMB];
    sqTreesApi.getTree({ id: currentAsset }).then(({ data: item }) => {
      const treeNode = item.item;
      if (treeNode && !_.isEmpty(treeNode.ancestors)) {
        breadcrumbs = _.concat(
          breadcrumbs,
          _.map(treeNode.ancestors, (ancestor) => {
            return toBreadcrumb({
              id: ancestor.id,
              name: ancestor.name,
              type: ancestor.type,
            });
          }),
        );
      }
      if (!treeNode) {
        return;
      }
      breadcrumbs = _.concat(
        breadcrumbs,
        toBreadcrumb({
          id: treeNode.id,
          name: treeNode.name,
          type: treeNode.type,
        }),
        SEARCH_BREADCRUMB,
      );
      getStoreForPane('main').actions.setBreadcrumbs(breadcrumbs);
    });
  }
  const isHierarchySearch = typeFilter.includes(ITEM_TABLE_TYPE) || typeFilter.includes(SeeqNames.Types.Hierarchy);

  const isScalingTableSearch = typeFilter.includes(SCALING_TABLE_TYPE);

  if (mode === 'search' && pane === 'main' && isHierarchySearch) {
    const searchAction = createHierarchySearchAction(pane);
    if (searchAction && selectedItemTable) {
      return searchAction() as Promise<void>;
    }
  }

  if (mode === 'search' && pane === 'main' && isScalingTableSearch) {
    const searchAction = createScalingTableSearchAction(pane);
    if (searchAction && selectedScalingTable) {
      return searchAction();
    }
  }

  if (pane === 'modal' && _.includes(searchTypes, SeeqNames.Types.AssetSelection)) {
    fetchAssetGroups(scopeIds);
  }
  return searchOrExplore(
    pane,
    mode,
    searchTypes,
    restrictExploration,
    scopeIds,
    excludeGloballyScoped || searchLocallyScoped,
  );
}

/**
 * Creates a search action for hierarchies based on the current state of the store when reloading. This is used
 * when the user is searching for hierarchies or item tables and reloads the page so that we can restore their
 * context.
 * @returns A function that performs the search action when called, or undefined if we are at the root of our search
 */
export function createHierarchySearchAction(pane: SearchPanes): () => Promise<void> | undefined {
  const store = getStoreForPane(pane);
  const { selectedHierarchy, selectedItemTable, currentHierarchyPath, typeFilter } = store.getState();
  const { setBreadcrumbs } = store.actions;
  let breadcrumbs: SearchBreadcrumb[] = [HOME_BREADCRUMB, SEARCH_BREADCRUMB];
  let action: () => Promise<void> = () => Promise.resolve(undefined);
  if (selectedItemTable) {
    action = () => setUpHierarchiesSearchForItemTable(pane, selectedItemTable, true);
  }
  // if the search type was the source table and a table was selected, add it to the breadcrumbs followed by the
  // hierarchy, otherwise just the hierarchy
  if (typeFilter.includes(ITEM_TABLE_TYPE) && selectedItemTable) {
    breadcrumbs.push(toBreadcrumb(selectedItemTable));
  }
  if (selectedHierarchy) {
    action = () => fetchHierarchyLayer(pane, selectedHierarchy, selectedHierarchy.id, [], true);
    breadcrumbs.push(toBreadcrumb(selectedHierarchy));
  }
  if (selectedHierarchy && _.size(currentHierarchyPath) > 0) {
    const nodePathAsBreadcrumbs = _.map(currentHierarchyPath, (node, index) => {
      return nodeAndPathToSelectedSearchItem(node, currentHierarchyPath.slice(0, index), selectedHierarchy);
    });
    nodePathAsBreadcrumbs.forEach((breadcrumb) => {
      breadcrumbs.push(toBreadcrumb(breadcrumb));
    });
    const leafItem = _.last(nodePathAsBreadcrumbs);
    const pathToLeaf = currentHierarchyPath.slice(0, currentHierarchyPath.length - 1);
    // if we have a path to leaf, we need to remove the last breadcrumb as it will be added again when fetching
    if (pathToLeaf.length < currentHierarchyPath.length) {
      breadcrumbs.pop();
    }
    if (leafItem) {
      action = () => fetchHierarchyLayer(pane, leafItem, selectedHierarchy.id, pathToLeaf, true);
    }
  }
  setBreadcrumbs(breadcrumbs);
  return action;
}

/**
 * Creates a search action for hierarchies based on the current state of the store when reloading. This is used
 * when the user is searching for hierarchies or item tables and reloads the page so that we can restore their
 * context.
 * @returns A function that performs the search action when called, or undefined if we are at the root of our search
 */
export function createScalingTableSearchAction(pane: SearchPanes): () => Promise<{ items: SelectedSearchItem[] }> {
  const store = getStoreForPane(pane);
  const { selectedScalingTable, selectedScalingTableRow, typeFilter } = store.getState();
  const { setBreadcrumbs } = store.actions;
  let breadcrumbs: SearchBreadcrumb[] = [HOME_BREADCRUMB, SEARCH_BREADCRUMB];
  let action: () => Promise<{ items: SelectedSearchItem[] }> = () => Promise.resolve({ items: [] });
  if (typeFilter.includes(SCALING_TABLE_TYPE) && selectedScalingTable) {
    breadcrumbs.push(toBreadcrumb(selectedScalingTable));
  }

  if (selectedScalingTableRow) {
    action = () => fetchScalingTableSearchItems(pane, selectedScalingTableRow, true);
    breadcrumbs.push(toBreadcrumb(selectedScalingTableRow));
  } else if (selectedScalingTable) {
    action = () => fetchScalingTableSearchItems(pane, selectedScalingTable, true);
  }

  setBreadcrumbs(breadcrumbs);
  return action;
}

/**
 * Initiate a new search and fetch the first page of results. If no search terms were entered the asset browser is
 * shown instead.
 *
 * @param pane - The name of the search pane, one of SearchPanes
 * @param searchTypes - The array of Item types to search if the typeFilter is empty
 * @param scopeIds - The array of Item Ids to search in, defaults to current workbook Id
 * @param excludeGloballyScoped - true to exclude globally scoped assets from the results, false otherwise.
 *
 * @return {Promise} A promise that resolves with the results
 */
export function searchItems(
  pane: SearchPanes,
  searchTypes: string[],
  scopeIds: string[] = [sqWorkbenchStore.stateParams.workbookId],
  excludeGloballyScoped = false,
) {
  return cancelGroup(cancellationGroup).finally(() => {
    const { initiateSearch } = getStoreForPane(pane).actions;
    initiateSearch();
    return setDatasources(pane, sqWorkbenchStore.stateParams.workbookId).then(() => {
      return fetchItemResults(pane, searchTypes, scopeIds, excludeGloballyScoped);
    });
  });
}

/**
 * If datasources aren't set in the store, get them via the API and set datasource and localDatasources in the store
 *
 * @param {String} pane - The name of the search pane, one of SearchPanes
 * @param {String} scopeId - The scope of the datasources to fetch
 * @param forceSet - if true, we'll always fetch the datasources
 * @return {Promise<void>} Promise that resolves when datasources are fetched and set, or immediately if
 *          datasources are already set
 */
export function setDatasources(pane: SearchPanes, scopeId?: string, forceSet = false): Promise<void> {
  const store = getStoreForPane(pane);
  const { datasources } = store.getState();
  const { setDatasources } = store.actions;
  if (!_.isEmpty(datasources) && !forceSet) {
    return Promise.resolve();
  }
  return sqDatasourcesApi
    .getDatasources({ scopedTo: scopeId, limit: MAX_RETURNED_RESULTS }, { cancellationGroup })
    .then(({ data: { datasources } }) => {
      setDatasources(datasources ?? []);
    });
}

/**
 * Initiate explore view at the asset and fetch the results
 *
 * @param pane - The name of the search pane, one of SearchPanes
 * @param asset - The id of the asset to show
 * @param searchTypes - The types to search
 * @param restrictExploration - If exploration should be restricted
 * @param scopeIds
 * @param excludeGloballyScoped - true to exclude globally scoped items from the results, false otherwise.
 * @return {Promise} A promise that resolves with the results
 */
export function exploreAssetSearchActions(
  pane: SearchPanes,
  asset: string,
  searchTypes?: string[],
  restrictExploration?: boolean,
  scopeIds: string[] = [sqWorkbenchStore.stateParams.workbookId],
  excludeGloballyScoped = false,
): Promise<unknown> {
  clearSelection();

  return cancelGroup(cancellationGroup).then(() => {
    return setDatasources(pane, sqWorkbenchStore.stateParams.workbookId).then(() => {
      const store = getStoreForPane(pane);
      const { typeFilter } = store.getState();
      const { exploreAsset } = store.actions;
      exploreAsset(asset);
      const types = searchTypes ?? (typeFilter.length > 0 ? typeFilter : SEARCH_TYPES);
      return fetchAssetResults(pane, types, restrictExploration ?? false, scopeIds, excludeGloballyScoped);
    });
  });
}

/**
 * Clears the search state and re-populates the root nodes.
 *
 * @param pane - The name of the search pane, one of SearchPanes
 * @param searchTypes - The types to search
 * @param restrictExploration - Used when exploring an asset
 * @param scopeIds - The array of Item Ids to search in, defaults to current workbook Id
 * @param excludeGloballyScoped - true to exclude globally scoped assets from the results, false otherwise.
 * @param initialItemTable - If set, will set the selectedItemTable in the store to this value and show hierarchies for
 * this item table
 * @param initialHierarchy - If set, will set the selectedHierarchy in the store to this value and show the
 * first  hierarchy layer for this hierarchy
 */
export function clear(
  pane: SearchPanes,
  searchTypes: string[],
  restrictExploration = false,
  scopeIds: string[] = [sqWorkbenchStore.stateParams.workbookId],
  excludeGloballyScoped = false,
  initialItemTable?: SelectedSearchItem,
  initialHierarchy?: SelectedSearchItem,
  initialScalingTable?: SelectedSearchItem,
) {
  const { resetSoft } = getStoreForPane(pane).actions;
  resetSoft();
  return initializeSearchActions(
    pane,
    searchTypes,
    restrictExploration,
    scopeIds,
    excludeGloballyScoped,
    initialItemTable,
    initialHierarchy,
    initialScalingTable,
  );
}

/**
 * Sets the ID of the item to highlight
 *
 * @param {String} pane - The name of the search pane, one of SearchPanes
 * @param {String} id- The ID of the item to highlight
 */
export function setHighlightItem(pane: SearchPanes, id: string) {
  const { setHighlightItem } = getStoreForPane(pane).actions;
  setHighlightItem(id);
}

/**
 * Refreshes the specified search pane
 *
 * @param pane - The name of the search pane, one of SearchPanes
 * @param searchTypes - The types to search
 * @param restrictExploration - Used when exploring an asset
 * @param scopeIds - The array of Item Ids to search in
 * @param excludeGloballyScoped - true to exclude globally scoped items from the results, false otherwise.
 */
export function refresh(
  pane: SearchPanes,
  searchTypes: string[],
  restrictExploration: boolean,
  scopeIds: string[],
  excludeGloballyScoped = false,
) {
  const store = getStoreForPane(pane);
  const { mode } = store.getState();
  return searchOrExplore(pane, mode, searchTypes, restrictExploration, scopeIds, excludeGloballyScoped);
}

export function searchOrExplore(
  pane: SearchPanes,
  mode: SearchModes,
  searchTypes: string[],
  restrictExploration: boolean,
  scopeIds: string[],
  excludeGloballyScoped = false,
) {
  switch (mode) {
    case 'search':
      return searchItems(pane, searchTypes, scopeIds, excludeGloballyScoped).catch((error) => {
        errorToast({ httpResponseOrError: error });
      });
    case 'tree':
    case 'overview': {
      const store = getStoreForPane(pane);
      const { currentAsset } = store.getState();
      return exploreAssetSearchActions(
        pane,
        currentAsset,
        searchTypes,
        restrictExploration,
        scopeIds,
        excludeGloballyScoped,
      ).catch((error) => {
        errorToast({ httpResponseOrError: error });
      });
    }
    default:
      return Promise.resolve();
  }
}

/**
 * Search for items usages and dispatch the results. Results are paginated and restricted to only those in the
 * global scope or in the provided workbook scopes
 *
 * @param item - the Item to find usages for
 * @param pane - The name of the search pane, one of SearchPanes
 * @param scopeIds - The array of workbookIds to search in
 * @param shouldClearBreadcrumbs - if the breadcrumbs should be wiped before appending (if calling from properties
 *   panel)
 */
export function fetchItemUsages(
  item: { id: string; name?: string; type?: string },
  pane: SearchPanes,
  scopeIds: string[],
  shouldClearBreadcrumbs: boolean,
) {
  const store = getStoreForPane(pane);
  const { initiateUsagesSearch, setBreadcrumbs, appendOrReplaceBreadcrumb, setResultsSuccess, finishSearch } =
    store.actions;
  initiateUsagesSearch();
  if (shouldClearBreadcrumbs) {
    setBreadcrumbs([HOME_BREADCRUMB, SEARCH_USAGES_BREADCRUMB]);
  }
  appendOrReplaceBreadcrumb(toBreadcrumb(item));
  return sqItemsApi
    .getItemUsages({ id: item.id, scope: scopeIds })
    .then(({ data: { items, next } }) => {
      setResultsSuccess(items ?? [], !_.isUndefined(next));
    })
    .catch((e) => {
      errorToast({ httpResponseOrError: e });
      return Promise.reject(e);
    })
    .finally(() => {
      finishSearch();
    });
}

/**
 * Search for items and dispatch the results. Results are filtered by the different filters provided by the search
 * store. They are further restricted to only those in the global scope or in the provided workbook scopes
 *
 * @param pane - The name of the search pane, one of SearchPanes
 * @param searchTypes - The array of Item types to search if the typeFilter is empty
 * @param scopeIds - The array of Item Ids to search in
 * @param excludeGloballyScoped - true to exclude globally scoped items from the results, false otherwise.
 */
export function fetchItemResults(
  pane: SearchPanes,
  searchTypes: string[],
  scopeIds: string[],
  excludeGloballyScoped = false,
) {
  const store = getStoreForPane(pane);
  const { isPaginating, items, currentAsset, sortBy } = store.getState();
  const { getSearchFilters, setResultsSuccess, finishSearch } = store.actions;
  const offset = isPaginating ? items.length : 0;
  let { filters, types } = getSearchFilters(searchTypes, {
    workbookId: sqWorkbenchStore.stateParams.workbookId,
    userDatasources: sqWorkbenchStore.userDatasources,
    includeNewDatasources: sqWorkbenchStore.includeNewDatasources,
  });
  if (excludeGloballyScoped) {
    filters = _.concat(filters, SeeqNames.API.Flags.ExcludeGloballyScoped);
  }
  return sqItemsApi
    .searchItemsPost(
      {
        filters: typeof filters === 'string' ? [filters] : filters,
        types: typeof types === 'string' ? [types] : types,
        scope: _.compact(scopeIds),
        asset: currentAsset,
        orderByFields: sortBy ? [OrderByFieldsEnum[sortBy as keyof typeof OrderByFieldsEnum]] : [],
        includeProperties: [
          SeeqNames.Properties.DisplayUnit,
          SeeqNames.Properties.TableType,
          SeeqNames.Properties.CreatedByModelInstance,
          SeeqNames.Properties.InstanceRetired,
        ],
        offset,
        limit: SEARCH_PER_PAGE,
      },
      { cancellationGroup },
    )
    .then(({ data }) => {
      addAssetsProperty(data);
      setResultsSuccess(data.items ?? [], !_.isUndefined(data.next));
      return data;
    })
    .catch((e) => {
      errorToast({ httpResponseOrError: e });
      return Promise.reject(e);
    })
    .finally(() => {
      finishSearch();
    });
}

/**
 * Gets a list of children using the tree endpoint
 *
 * @param pane - The name of the search pane, one of SearchPanes
 * @param searchTypes - The types to search (default SEARCH_TYPES)
 * @param restrictExploration - If exploration should be restricted
 * @param scopeIds - Ids of scoped assets, default is workbookId
 * @param excludeGloballyScoped - true to exclude globally scoped items from the results, false otherwise.
 * @return {Promise} a promise that will resolve when the results have been fetched
 */
export function fetchAssetResults(
  pane: SearchPanes,
  searchTypes: string[],
  restrictExploration: boolean,
  scopeIds: string[],
  excludeGloballyScoped: boolean,
) {
  const store = getStoreForPane(pane);
  const { isPaginating, items, currentAsset } = store.getState();
  const { getSearchFilters, setBreadcrumbs, setResultsSuccess, finishSearch } = store.actions;
  const offset = isPaginating ? items.length : 0;
  const { types } = getSearchFilters(searchTypes, {
    workbookId: sqWorkbenchStore.stateParams.workbookId,
    userDatasources: sqWorkbenchStore.userDatasources,
    includeNewDatasources: sqWorkbenchStore.includeNewDatasources,
  });

  const getTreePromise = _.isEmpty(currentAsset)
    ? sqTreesApi.getTreeRootNodes(
        {
          scope: scopeIds,
          excludeGloballyScoped,
          limit: ASSET_TREE_SEARCH_LIMIT,
          offset,
          properties: [`${SeeqNames.Properties.TreeType}!=${CREATED_BY_SEEQ_WORKBENCH}`],
        },
        { cancellationGroup },
      )
    : sqTreesApi.getTree(
        {
          id: currentAsset,
          scope: scopeIds,
          excludeGloballyScoped,
          limit: ASSET_TREE_SEARCH_LIMIT,
          offset,
        },
        { cancellationGroup },
      );

  return Promise.resolve(getTreePromise)
    .then((response: AxiosResponse<AssetTreeOutputV1>) => {
      let breadcrumbs = [HOME_BREADCRUMB];
      const filteredItems = restrictExploration
        ? _.filter(response.data.children, (child) => _.includes(types, child.type))
        : response.data.children;
      if (response.data.item) {
        const ancestors = response.data.item.ancestors;
        breadcrumbs = breadcrumbs.concat(ancestors ?? []).concat(response.data.item);
      }

      setBreadcrumbs(breadcrumbs);
      const items = _.map(filteredItems, (child) =>
        _.assign(child, {
          ancestors: _.concat(response.data.item?.ancestors ?? [], response.data.item ?? []),
        }),
      );
      setResultsSuccess(items, !_.isUndefined(response.data.next));

      return { ...response.data, items };
    })
    .catch((e) => {
      errorToast({ httpResponseOrError: e });
      return Promise.reject(e);
    })
    .finally(() => finishSearch());
}

/**
 * Set up the SearchWidget to show Hierarchies for a given Item Table.
 * @param pane
 * @param item - should be of type TableDefinition, and have tableType as ITEM or ITEMCSV
 */
export const setUpHierarchiesSearchForItemTable = async (
  pane: SearchPanes,
  item: SelectedSearchItem | ItemSearchPreviewV1,
  isSearch = false,
) => {
  const store = getStoreForPane(pane);
  const { setMode, exploreItemHierarchy, setSelectedItemTable, appendOrReplaceBreadcrumb } = store.actions;
  const mode = isSearch ? 'search' : 'item-hierarchies';
  if (!isSearch) {
    setMode(mode);
  }
  exploreItemHierarchy();
  setSelectedItemTable(item);
  appendOrReplaceBreadcrumb(item);
  await fetchHierarchiesForSearchResults(pane, item);
};

/**
 * Use this to set up the SearchWidget to show a layer in the Hierarchy when none of the store has been set up yet.
 * e.g. from details pane path
 *
 * @param pane
 * @param hierarchyId
 * @param node - Node whose child layer we want to show
 * @param nodePath - Path from the Hierarchy root to the given node, excluding the given node
 */
export const exploreHierarchyNode = async (
  pane: SearchPanes,
  hierarchyId: string,
  node?: HierarchyNodeOutputV1,
  nodePath: HierarchyNodeOutputV1[] = [],
) => {
  const { data: hierarchy } = await sqHierarchiesApi.getHierarchy({ id: hierarchyId });
  const { data: itemTable } = await sqItemsApi.getItemAndAllProperties({ id: hierarchy.tableDefinition.id });
  const store = getStoreForPane(pane);
  const { exploreItemHierarchy, setBreadcrumbs, setSelectedItemTable, setSelectedHierarchy } = store.actions;
  exploreItemHierarchy();
  const nodeAsItem: SelectedSearchItem | undefined = node
    ? nodeAndPathToSelectedSearchItem(node, nodePath, hierarchy)
    : undefined;
  setBreadcrumbs([HOME_BREADCRUMB, toBreadcrumb(itemTable), ...(nodeAsItem?.ancestors ?? []).map(toBreadcrumb)]);
  setSelectedItemTable(itemTable);
  setSelectedHierarchy(hierarchy);
  await fetchHierarchyLayer(pane, nodeAsItem ?? hierarchy, hierarchyId, nodePath);
};

/**
 *
 * Fetch hierarchies for a given Item Table and set up the store to show them
 * @param pane
 * @param item
 */
export const fetchHierarchiesForSearchResults = async (
  pane: SearchPanes,
  item: SelectedSearchItem | ItemSearchPreviewV1,
) => {
  const store = getStoreForPane(pane);
  const { setResultsSuccess, finishSearch } = store.actions;
  try {
    const response = await sqHierarchiesApi.getHierarchies({
      tableDefinitionId: item.id,
      limit: 1000,
    });
    const hierarchies = response.data.hierarchies;
    if (!_.isEmpty(hierarchies)) {
      setResultsSuccess(hierarchies ?? [], false);
    }
  } catch (e) {
    errorToast({ httpResponseOrError: e });
    return Promise.reject(e);
  } finally {
    finishSearch();
  }
};

export function setCurrentNodePath(pane: SearchPanes, path: HierarchyNodeOutputV1[]): void {
  const { setCurrentHierarchyPath } = getStoreForPane(pane).actions;
  setCurrentHierarchyPath(path);
}

export function setSelectedHierarchy(pane: SearchPanes, hierarchy?: SelectedSearchItem): void {
  const { setSelectedHierarchy } = getStoreForPane(pane).actions;
  setSelectedHierarchy(hierarchy);
}

export function setSelectedScalingTableRow(pane: SearchPanes, scalingTableRow?: SelectedSearchItem): void {
  const { setSelectedScalingTableRow } = getStoreForPane(pane).actions;
  setSelectedScalingTableRow(scalingTableRow);
}

export function resetTreeBuilderSearchParams(pane: SearchPanes): void {
  const { setSelectedHierarchy, setSelectedItemTable } = getStoreForPane(pane).actions;
  setCurrentNodePath(pane, []);
  setSelectedHierarchy(undefined);
  setSelectedItemTable(undefined);
}

export function resetScalingTableSearchParams(pane: SearchPanes): void {
  const { setSelectedScalingTable, setSelectedScalingTableRow } = getStoreForPane(pane).actions;
  setSelectedScalingTable(undefined);
  setSelectedScalingTableRow(undefined);
}

/**
 * Append the breadcrumb to breadcrumbs or lose everything after the provided breadcrumb if it already exists
 *
 * @param pane - The search pane, one of SearchPanes
 * @param breadcrumb - a breadcrumb to append or slice at
 */
export function appendOrReplaceBreadcrumb(pane: SearchPanes, breadcrumb: SelectedSearchItem): void {
  const { appendOrReplaceBreadcrumb } = getStoreForPane(pane).actions;
  appendOrReplaceBreadcrumb(toBreadcrumb(breadcrumb));
}

async function loadHierarchyItems(
  hierarchyId: string,
  item: SelectedSearchItem,
  updatedNodePath: HierarchyNodeOutputV1[],
  pane: 'main' | 'modal',
) {
  const store = getStoreForPane(pane);
  const { setResultsSuccess, finishSearch } = store.actions;
  try {
    const items = await getHierarchyLayerAsItemsWithAncestors(hierarchyId, item, updatedNodePath);
    if (!_.isEmpty(items)) {
      setResultsSuccess(items, false);
    }
  } catch (e) {
    errorToast({ httpResponseOrError: e });
    return Promise.reject(e);
  } finally {
    finishSearch();
  }
}

/**
 * Fetch a layer in the Hierarchy and update the store to show this layer. Used when clicking a Hierarchy or any
 * node in a Hierarchy
 * @param pane
 * @param item - the selected item, should be of type Hierarchy or a node in the hierarchy
 * @param hierarchyId - the id of the hierarchy being explored
 * @param currentNodePath - the path from the root to the parent of the given item, empty if item is a hierarchy
 */
export async function fetchHierarchyLayer(
  pane: SearchPanes,
  item: SelectedSearchItem,
  hierarchyId: string,
  currentNodePath: HierarchyNodeOutputV1[],
  isSearch = false,
): Promise<void> {
  return cancelGroup(cancellationGroup).then(() => {
    const store = getStoreForPane(pane);
    const { setMode, exploreItemHierarchy, setSelectedHierarchy, appendOrReplaceBreadcrumb } = store.actions;
    let updatedNodePath = currentNodePath;
    if (!isSearch) {
      setMode('hierarchy-nodes');
    }
    exploreItemHierarchy();
    if (item.type === SeeqNames.Types.Hierarchy) {
      setSelectedHierarchy(item);
      setCurrentNodePath(pane, []);
    } else {
      if (item.node) {
        updatedNodePath = updatedNodePath.concat(item.node);
        setCurrentNodePath(pane, updatedNodePath);
      }
    }
    appendOrReplaceBreadcrumb(toBreadcrumb(item));
    return loadHierarchyItems(hierarchyId, item, updatedNodePath, pane);
  });
}

export function fetchScalingTableSearchItems(
  pane: SearchPanes,
  item: SelectedSearchItem | ItemSearchPreviewV1,
  isSearch = false,
): Promise<{ items: SelectedSearchItem[] }> {
  return cancelGroup(cancellationGroup).then(async () => {
    const store = getStoreForPane(pane);
    const {
      setMode,
      exploreScalingTable,
      appendOrReplaceBreadcrumb,
      setSelectedScalingTable,
      setSelectedScalingTableRow,
      setResultsSuccess,
      finishSearch,
    } = store.actions;

    const { isPaginating, items: currentItems } = store.getState();

    if (!isPaginating) {
      if (!isSearch) {
        setMode('scaling-tables-tree');
      }
      exploreScalingTable();
      appendOrReplaceBreadcrumb(toBreadcrumb(item));

      if (item.type === SeeqNames.Types.TableDefinition) {
        setSelectedScalingTable(item);
        setSelectedScalingTableRow(undefined);
      } else {
        setSelectedScalingTableRow(item);
      }
    }

    // After a page reload, breadcrumbs are dehydrated without ancestors. Reconstruct them
    // from the breadcrumb list so getScalingTableLayerForDataTab can find the tableDefinition.
    let itemWithAncestors = item;
    if (!item.ancestors) {
      const { breadcrumbs } = store.getState();
      const breadcrumbIndex = breadcrumbs.findIndex(({ id }) => id === item.id);
      if (breadcrumbIndex > 0) {
        // Home can be absent if its search was cancelled by opening a table. Keep the first real ancestor.
        itemWithAncestors = {
          ...item,
          ancestors: breadcrumbs
            .slice(0, breadcrumbIndex)
            .filter((breadcrumb) => breadcrumb.type !== 'VIEW_MODE')
            .map(toAncestorItem),
        };
      }
    }

    try {
      const { scalingTableViewMode } = store.getState();
      const isFlatMode = scalingTableViewMode === 'flat';
      const offset = isPaginating ? currentItems.length : 0;
      const { items, hasMore } = await getScalingTableLayerForDataTab(itemWithAncestors, isFlatMode, offset);
      setResultsSuccess(items, hasMore);
      return { items };
    } catch (e) {
      errorToast({ httpResponseOrError: e });
      return Promise.reject(e);
    } finally {
      finishSearch();
    }
  });
}

export function clearSelection(): void {
  const { clearSelectedItems } = getStoreForPane('main').actions;
  clearSelectedItems();
}

/**
 * Restore an archived item and refresh the search pane for the current asset
 *
 * @param item The item to restore
 * @param [pane] - The name of the search pane, one of SearchPanes (default SearchPanes.MAIN)
 * @param [searchTypes] - The types to search (default SEARCH_TYPES)
 * @param [isPinned] - Is this a pinned item (default false)
 * @param [restrictExploration] - If exploration should be restricted (default false)
 * @param [scopeIds] - Ids of scoped assets (default is workbookId)
 */
export function restoreItem(
  item: { id: string; name: string; type: string },
  pane: SearchPanes = 'main',
  searchTypes: string[] = SEARCH_TYPES,
  isPinned = false,
  restrictExploration = false,
  scopeIds: string[] = [sqWorkbenchStore.stateParams.workbookId],
) {
  return sqItemsApi
    .setProperty({ value: false }, { id: item.id, propertyName: SeeqNames.Properties.Archived })
    .then(() => {
      const store = getStoreForPane(pane);
      const { mode } = store.getState();
      return searchOrExplore(pane, mode, searchTypes, restrictExploration, scopeIds).then(() => {
        if (isPinned && mode === 'overview') {
          fetchPinnedItems(sqWorkbenchStore.stateParams.workbookId);
        }
        successToast({
          messageKey: 'RESTORE_ITEM.SUCCESS',
          messageParams: { ITEM_NAME: item.name },
        });
      });
    })
    .catch(() => {
      errorToast({
        messageKey: 'RESTORE_ITEM.ERROR',
        messageParams: { ITEM_NAME: item.name },
      });
    });
}

/**
 * Retrieve the archive API promise for an item
 *
 * @param item The item to archive/delete
 * @return {Promise} The archive API
 */
export function getDeleteAPI(item: { type: string; id: string }) {
  switch (item.type) {
    case API_TYPES.DISPLAY:
      return sqDisplaysApi.archiveDisplay({ id: item.id });
    case API_TYPES.DISPLAY_TEMPLATE:
      return sqDisplayTemplatesApi.archiveDisplayTemplate({ id: item.id });
    default:
      return sqItemsApi.archiveItem({ id: item.id });
  }
}

/**
 * Delete an item with an option to show restore button. Upon success calls searchOrExplore.
 *
 * @param item The item to delete
 * @param messageDeleted - The messageKey to show when the item is successfully deleted
 * @param messageDeleteError - The messageKey to show when an error occurs deleting the item
 * @param [messageDeletedParams] - Parameters for messageDeleted
 * @param [messageDeletedErrorParams] - Parameters for messageDeletedError
 * @param [isPinned] - Is this a pinned item (default false)
 * @param [restoreButton] - True to show a Toast message with restore button (default false)
 * @param [restrictExploration] - If exploration should be restricted (default false)
 * @param [pane] - The name of the search pane, one of SearchPanes (default SearchPanes.MAIN)
 * @param [searchTypes] - The types to search (default SEARCH_TYPES)
 * @param [scopeIds] - Ids of scoped assets (default is workbookId)
 */
export function deleteItem(
  item: { id: string; name: string; type: string },
  messageDeleted: string,
  messageDeleteError: string,
  messageDeletedParams: TOptions = {},
  messageDeletedErrorParams: TOptions = {},
  isPinned = false,
  restoreButton = false,
  restrictExploration = false,
  pane: SearchPanes = 'main',
  searchTypes: string[] = SEARCH_TYPES,
  scopeIds: string[] = [sqWorkbenchStore.stateParams.workbookId],
) {
  return getDeleteAPI(item)
    .then(() => {
      const store = getStoreForPane(pane);
      const { mode } = store.getState();
      return searchOrExplore(pane, mode, searchTypes, restrictExploration, scopeIds).then(() => {
        if (isPinned) {
          fetchPinnedItems(sqWorkbenchStore.stateParams.workbookId);
        }

        if (restoreButton) {
          successToast({
            messageKey: messageDeleted,
            messageParams: messageDeletedParams,
            buttonLabelKey: 'RESTORE',
            buttonAction: () => restoreItem(item, pane, searchTypes, isPinned, restrictExploration, scopeIds),
          });
        } else {
          successToast({
            messageKey: messageDeleted,
            messageParams: messageDeletedParams,
          });
        }
      });
    })
    .catch(() => {
      errorToast({
        messageKey: messageDeleteError,
        messageParams: messageDeletedErrorParams,
      });
    });
}

/**
 * Resets the async initialized flag for the given pane and clears the current search params
 * @param pane - the name of the search pane
 */
export function reset(pane: SearchPanes) {
  const { setAsyncInitialized, resetSoft } = getStoreForPane(pane).actions;
  setAsyncInitialized(false);
  resetSoft();
}

/**
 * Helper function that returns the correct store based on the pane.
 *
 * @param {String} pane - The name of the search pane, one of SearchPanes
 *
 * @return {Object} The search store that is used for the specified pane
 */
export function getStoreForPane(pane: SearchPanes) {
  return pane === 'main' ? sqSearchStore : sqModalSearchStore;
}

export const getScalingTableLayerForDataTab = async (
  searchItem: SelectedSearchItem | ItemSearchPreviewV1,
  isFlatMode = false,
  offset = 0,
): Promise<{ items: SelectedSearchItem[]; hasMore: boolean }> => {
  const isTableDefinition = searchItem.type === SeeqNames.Types.TableDefinition;
  const ancestors = searchItem.ancestors ?? [];
  const tableDefinition = isTableDefinition
    ? searchItem
    : ancestors.find(({ type }) => type === SeeqNames.Types.TableDefinition);
  if (!tableDefinition) return { items: [], hasMore: false };

  const tableDefinitionItem = toAncestorItem(tableDefinition);
  const treePathAncestors = ancestors.filter(({ type }) => type === HierarchyNodeType);
  const groupKeys = isTableDefinition ? [] : [...treePathAncestors.map(({ id }) => id!), searchItem.id!];

  // Use flat=true only at the table root (groupKeys empty); drilling into a row always uses tree mode.
  const useFlatMode = isFlatMode && isTableDefinition;
  const { data } = await sqTablesApi.queryTableAsTree({
    tableId: tableDefinition.id,
    groupKeys,
    flat: useFlatMode,
    limit: SEARCH_PER_PAGE,
    offset,
  });
  const nodes = data.nodes ?? [];

  const parentAsAncestor = !isTableDefinition
    ? { id: searchItem.id!, name: searchItem.name!, type: HierarchyNodeType }
    : undefined;

  const groupNodeAncestors = [
    tableDefinitionItem,
    ...treePathAncestors,
    ...(parentAsAncestor ? [parentAsAncestor] : []),
  ];

  const items = nodes.map((node) => {
    // Actual rows in the scaling table are the only ones with a node id that is a guid.
    const isTableRow = node.group && validateGuid(node.id);

    // For leaf nodes, always use the owning row id as the parent ancestor so that
    // getScalingTableItemTrendProps receives the correct rowId in ancestors[1].
    const leafRowAncestor =
      node.group || (isTableDefinition && !node.rowId)
        ? undefined
        : {
            id: node.rowId ?? searchItem.id!,
            name: (node.rowId ?? searchItem.id) === searchItem.id ? searchItem.name! : '',
            type: HierarchyNodeType,
          };

    return {
      id: node.id,
      name: node.name,
      hasActiveChildren: node.group,
      isTableRow,
      // Real table rows carry their actual item type. Hierarchy path nodes always get HierarchyNodeType
      // regardless of any adopted type the backend may assign them for display purposes.
      type: node.group ? (isTableRow ? node.type : HierarchyNodeType) : node.type,
      ancestors: node.group ? groupNodeAncestors : [tableDefinitionItem, ...(leafRowAncestor ? [leafRowAncestor] : [])],
    };
  });

  return { items, hasMore: data.hasMore };
};

export const getHierarchyLayerAsItemsWithAncestors = async (
  hierarchyId: string,
  currentItemWithNode: SelectedSearchItem,
  currentNodePath: HierarchyNodeOutputV1[],
): Promise<SelectedSearchItem[]> => {
  const columnIds = currentNodePath.length > 0 ? currentNodePath.map((node) => node.columnId) : undefined;
  const columnValues =
    currentNodePath.length > 0 ? currentNodePath.map((node) => node.itemMetadata?.id ?? node.displayName) : undefined;
  const response = await sqHierarchiesApi.getHierarchyNodes({
    id: hierarchyId,
    columnIds,
    columnValues,
  });
  const nodes = response.data.nodes;

  return (
    nodes?.map((node) => {
      const ancestors = [
        ...(currentItemWithNode.ancestors || []),
        {
          id: currentItemWithNode.id,
          name: currentItemWithNode.name,
          type: currentItemWithNode.type,
          node: currentItemWithNode.node,
        },
      ];
      return nodeToSelectedSearchItem(node, ancestors);
    }) || []
  );
};
